import { useContext, useState } from 'react'
import { useWatch } from 'react-hook-form'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { z } from 'zod'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { customDomainThemeModes } from 'app/frontend/types/custom/CustomDomainThemeMode'
import { themeVariables } from 'app/frontend/stylesheets/theme-defaults'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useTheme } from '@/components/theme-provider'
import {
  CustomDomainHeaderTitleType,
  customDomainHeaderDisplays,
  customDomainHeaderTitleTypes,
  customDomainHeaderTypes,
  customDomainHomepageImageStyles,
  customDomainMainMenuContents,
  textStyles,
} from 'app/frontend/types/custom/CustomDomainHeaderConfig'
import { PreviewToggle, ThemeColorFields, ThemeModeField } from '../ThemeFields'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import CustomDomainHostFormContent from '../CustomDomainHosts'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import { useTranslation } from 'react-i18next'
import { FormGroup } from '@/components/ui/form'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import FileField from 'app/frontend/base/components/form-fields/FileField'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Link } from 'react-router-dom'
import { FormStateContext } from 'app/frontend/base/config/context/forms'
import { Button } from '@/components/ui/button'
import CustomDomainNewHostModal, {
  MAX_HOSTS,
} from '../CustomDomainNewHostModal'
import { OverviewRuntimeContext } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/context'
import { uploadFile } from 'app/frontend/base/api/team'

export type CustomDomainSettingsEntity = (wowaudit.Team | wowaudit.Guild) & {
  customDomainAttributes?: wowaudit.CustomDomainAttributes
}

const buildFormSchema = (
  headerTitleTypes: readonly [
    CustomDomainHeaderTitleType,
    ...CustomDomainHeaderTitleType[],
  ],
) =>
  z.object({
    customDomainAttributes: z.object({
      id: z.number().optional(),
      themeMode: z.enum(customDomainThemeModes).optional(),
      headerShowName: z.boolean(),
      generalTheme: z.object({
        '--radius-sm-custom': z.string().optional(),
        '--font-family-custom': z.string().optional(),
      }),
      lightModeTheme: z.object(
        Object.fromEntries(
          themeVariables.map((name) => [name, z.string().optional()]),
        ),
      ),
      darkModeTheme: z.object(
        Object.fromEntries(
          themeVariables.map((name) => [name, z.string().optional()]),
        ),
      ),
      lightModeBodyBackgroundPath: z.string().optional().nullable(),
      darkModeBodyBackgroundPath: z.string().optional().nullable(),
      lightModeHeaderLogoPath: z.string().optional().nullable(),
      darkModeHeaderLogoPath: z.string().optional().nullable(),
      headerConfig: z.object({
        backgroundImage: z.string().optional().nullable(),
        headerDisplay: z.enum(customDomainHeaderDisplays).optional(),
        headerType: z.enum(customDomainHeaderTypes).optional(),
        hideOverviewMenuItem: z.boolean().optional(),
        textStyle: z.enum(textStyles).optional(),
        transparentCards: z.boolean().optional(),
        hideLinks: z.boolean().optional(),
        hideBanner: z.boolean().optional(),
        noImage: z.boolean().optional(),
        height: z.number().nullable().optional(),
        mainMenuContent: z.enum(customDomainMainMenuContents).optional(),
        titleType: z.enum(headerTitleTypes).optional(),
        subtitleType: z.enum(headerTitleTypes).optional(),
        homepageImageStyle: z.enum(customDomainHomepageImageStyles).optional(),
      }),
    }),
  })

interface Props {
  entityType: 'team' | 'guild'
  entity: CustomDomainSettingsEntity
  guild: wowaudit.Guild
  team?: wowaudit.Team
  ownerLabel: 'team' | 'guild'
  premiumPath: string
  titleTypeOptions: readonly [
    CustomDomainHeaderTitleType,
    ...CustomDomainHeaderTitleType[],
  ]
  subtitleTypeOptions: readonly [
    CustomDomainHeaderTitleType,
    ...CustomDomainHeaderTitleType[],
  ]
  subtitlePlaceholder: string
  uploadOwnerFile: (file: File) => Promise<string>
}

const HomepageImageStyleField = ({
  previewHeaderConfig,
}: {
  previewHeaderConfig: (
    key: keyof wowaudit.CustomDomain['headerConfig'],
    value: wowaudit.CustomDomain['headerConfig'][keyof wowaudit.CustomDomain['headerConfig']],
  ) => void
}) => {
  const { t } = useTranslation()
  const lightModeBodyBackgroundPath = useWatch({
    name: 'customDomainAttributes.lightModeBodyBackgroundPath',
  })
  const darkModeBodyBackgroundPath = useWatch({
    name: 'customDomainAttributes.darkModeBodyBackgroundPath',
  })
  const hasBodyBackground = !!(
    lightModeBodyBackgroundPath || darkModeBodyBackgroundPath
  )

  return (
    <SelectField
      layout='box'
      name='customDomainAttributes.headerConfig.homepageImageStyle'
      options={customDomainHomepageImageStyles}
      customPlaceholder='Solid'
      disabled={hasBodyBackground}
      customDescription={
        hasBodyBackground ? (
          <span className='text-info-foreground'>
            {t(
              'forms.fields.team.customDomainAttributes.headerConfig.homepageImageStyle.bodyBackgroundDescription',
            )}
          </span>
        ) : undefined
      }
      optionLabelFn={(option) =>
        t(
          `forms.fields.team.customDomainAttributes.headerConfig.homepageImageStyle.options.${option}`,
        )
      }
      onChange={(value) => {
        previewHeaderConfig(
          'homepageImageStyle',
          value as wowaudit.CustomDomain['headerConfig']['homepageImageStyle'],
        )
      }}
    />
  )
}

export const CustomDomainSettingsSection = ({
  entityType,
  entity,
  guild,
  team,
  ownerLabel,
  premiumPath,
  titleTypeOptions,
  subtitleTypeOptions,
  subtitlePlaceholder,
  uploadOwnerFile,
}: Props) => {
  const { t } = useTranslation()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const {
    data: { customDomain: originalCustomDomain },
  } = usePageInfo()
  const { handleSectionChange } = useContext(FormStateContext)
  const { customDomain, setCustomDomainPreview } = useCustomDomain()
  const { currentScheme } = useTheme()
  const [previewing, setPreviewing] = useState(!!customDomain)
  const [selectedTab, setSelectedTab] = useState<string>(currentScheme)
  const [modalOpen, setModalOpen] = useState(false)
  const formSchema = buildFormSchema(titleTypeOptions)

  const previewHeaderConfig = (
    key: keyof wowaudit.CustomDomain['headerConfig'],
    value: wowaudit.CustomDomain['headerConfig'][keyof wowaudit.CustomDomain['headerConfig']],
  ) => {
    if (previewing && customDomain) {
      setCustomDomainPreview({
        ...customDomain,
        headerConfig: {
          ...customDomain.headerConfig,
          [key]: value,
        },
      })
    }
  }

  const isPremium = guild?.premiumPoints ? guild.premiumPoints >= 25 : false

  const activeHostCount =
    entity.customDomainAttributes?.hostsAttributes.length ?? 0
  const transformValues = (values: z.infer<typeof formSchema>) => {
    if (entityType === 'team') return values

    return {
      ...values,
      customDomainAttributes: {
        ...values.customDomainAttributes,
        headerConfig: {
          ...values.customDomainAttributes.headerConfig,
          headerDisplay: 'always',
        },
      },
    }
  }

  return (
    <>
      <h4>Custom domain</h4>

      <p className='mb-6'>
        You can set up your own custom domain here, creating a fully standalone
        and personalized website for your {ownerLabel}. Here is a{' '}
        <a
          href='https://avalerion.eu'
          target='_blank'
          rel='noreferrer'
          className='text-primary hover:text-primary/90'
        >
          demo
        </a>{' '}
        of what it can look like.
      </p>

      {isPremium ? null : (
        <Alert variant='warning' className='mb-6'>
          <AlertTitle>{t('errors.custom_domain.not_premium_title')}</AlertTitle>
          <AlertDescription className='flex items-center justify-between gap-2'>
            Custom domains require 25 boosts to be active, your guild has{' '}
            {guild?.premiumPoints ?? 0} boosts. You can still set up your domain
            here and preview your custom theme.
            <Link
              to={premiumPath}
              onClick={() => handleSectionChange('premium')}
            >
              <Button
                variant='outline'
                type='button'
                size='sm'
                className='text-foreground'
              >
                {t('shared.premium')}
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <h5 className='mb-4'>Domains</h5>

      <CustomDomainHostFormContent entityType={entityType} entity={entity} />

      <div className='flex justify-end mb-8'>
        <Button
          type='button'
          size='sm'
          className='mt-2'
          variant='outline'
          disabled={activeHostCount >= MAX_HOSTS}
          onClick={() => setModalOpen(true)}
        >
          Add domain
        </Button>
      </div>

      <OverviewRuntimeContext.Provider
        value={{
          isOverviewDataLoading: false,
          ownerPath: entity.path,
          ownerName: entity.name,
          uploadFile: uploadOwnerFile,
          recentNewsEditPermission: {
            section: 'base',
            requirement: 'admin',
            requireAllTeams: entityType === 'guild',
          },
        }}
      >
        <FormWrapper
          formSchema={formSchema}
          entityType={entityType}
          entity={entity}
          fieldEntityType='team'
          transformValues={transformValues}
        >
          <h5 className='flex items-center justify-between gap-4 mb-4'>
            <span>Theme</span>
            {originalCustomDomain ? null : (
              <PreviewToggle
                previewing={previewing}
                onToggle={setPreviewing}
                selectedTab={selectedTab}
                guild={guild}
                team={team}
              />
            )}
          </h5>

          <ThemeModeField previewing={previewing} />

          <SelectField
            layout='box'
            customPlaceholder='4px'
            name='customDomainAttributes.generalTheme.--radius-sm-custom'
            onChange={(value) => {
              previewing
                ? document.documentElement.style.setProperty(
                    '--radius-sm-custom',
                    value as string,
                  )
                : null
            }}
            disableSorting={true}
            options={Array.from({ length: 21 }, (_, i) => `${i}px`)}
          />

          <TextField
            name='customDomainAttributes.generalTheme.--font-family-custom'
            onBlur={(e) => {
              if (previewing)
                document.documentElement.style.setProperty(
                  '--font-family-custom',
                  e.target.value,
                )
            }}
            layout='box'
          />

          <ThemeColorFields
            previewing={previewing}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />

          <h5 className='mt-6 mb-4'>Navigation menu</h5>

          <CheckboxField
            layout='box'
            name='customDomainAttributes.headerShowName'
            onChange={(value) => {
              if (previewing && customDomain) {
                setCustomDomainPreview({
                  ...customDomain,
                  headerShowName: value as boolean,
                })
              }
            }}
          />

          <CheckboxField
            layout='box'
            name='customDomainAttributes.headerConfig.hideOverviewMenuItem'
            onChange={(value) => {
              previewHeaderConfig(
                'hideOverviewMenuItem',
                value as wowaudit.CustomDomain['headerConfig']['hideOverviewMenuItem'],
              )
            }}
          />

          {entityType === 'guild' ? (
            <SelectField
              name='customDomainAttributes.headerConfig.mainMenuContent'
              options={customDomainMainMenuContents}
              customPlaceholder='Team switcher and team menu'
              optionLabelFn={(option) =>
                t(
                  `forms.fields.team.customDomainAttributes.headerConfig.mainMenuContent.options.${option}`,
                )
              }
              onChange={(value) => {
                previewHeaderConfig(
                  'mainMenuContent',
                  value as wowaudit.CustomDomain['headerConfig']['mainMenuContent'],
                )
              }}
            />
          ) : null}

          <SelectField
            layout='box'
            name='customDomainAttributes.headerConfig.headerType'
            options={customDomainHeaderTypes}
            customPlaceholder='Top'
            onChange={(value) => {
              previewHeaderConfig(
                'headerType',
                value as wowaudit.CustomDomain['headerConfig']['headerType'],
              )
            }}
          />

          <h5 className='mt-6 mb-4'>Page header</h5>

          {entityType === 'team' ? (
            <SelectField
              layout='box'
              name='customDomainAttributes.headerConfig.headerDisplay'
              options={customDomainHeaderDisplays}
              customPlaceholder='Always'
              onChange={(value) => {
                previewHeaderConfig(
                  'headerDisplay',
                  value as wowaudit.CustomDomain['headerConfig']['headerDisplay'],
                )
              }}
            />
          ) : null}

          <SelectField
            layout='box'
            name='customDomainAttributes.headerConfig.textStyle'
            options={textStyles}
            customPlaceholder='With shadow'
            optionLabelFn={(option) =>
              t(
                `forms.fields.team.customDomainAttributes.headerConfig.textStyle.options.${option}`,
              )
            }
            onChange={(value) => {
              previewHeaderConfig(
                'textStyle',
                value as wowaudit.CustomDomain['headerConfig']['textStyle'],
              )
            }}
          />

          <HomepageImageStyleField previewHeaderConfig={previewHeaderConfig} />

          <CheckboxField
            layout='box'
            name='customDomainAttributes.headerConfig.transparentCards'
            onChange={(value) => {
              previewHeaderConfig(
                'transparentCards',
                value as wowaudit.CustomDomain['headerConfig']['transparentCards'],
              )
            }}
          />

          <FileField
            name='customDomainAttributes.headerConfig.backgroundImage'
            customPlaceholder={`https://data.wowaudit.com/img/banners/${gameVersion}.jpg`}
            onBlur={(e) => {
              previewHeaderConfig(
                'backgroundImage',
                e.target
                  .value as wowaudit.CustomDomain['headerConfig']['backgroundImage'],
              )
            }}
          />

          <FormGroup className='[&>div]:w-full'>
            <CheckboxField
              layout='box'
              name='customDomainAttributes.headerConfig.noImage'
              onChange={(value) => {
                previewHeaderConfig(
                  'noImage',
                  value as wowaudit.CustomDomain['headerConfig']['noImage'],
                )
              }}
            />

            <CheckboxField
              layout='box'
              name='customDomainAttributes.headerConfig.hideBanner'
              onChange={(value) => {
                previewHeaderConfig(
                  'hideBanner',
                  value as wowaudit.CustomDomain['headerConfig']['hideBanner'],
                )
              }}
            />

            <CheckboxField
              layout='box'
              name='customDomainAttributes.headerConfig.hideLinks'
              onChange={(value) => {
                previewHeaderConfig(
                  'hideLinks',
                  value as wowaudit.CustomDomain['headerConfig']['hideLinks'],
                )
              }}
            />
          </FormGroup>

          <SelectField
            layout='box'
            name='customDomainAttributes.headerConfig.titleType'
            optionLabelFn={(option) =>
              t(
                `forms.fields.team.customDomainAttributes.headerConfig.titleType.options.${option}`,
              )
            }
            options={titleTypeOptions}
            customPlaceholder='Guild name'
            onChange={(value) => {
              previewHeaderConfig('titleType', value as string)
            }}
          />

          <SelectField
            layout='box'
            name='customDomainAttributes.headerConfig.subtitleType'
            optionLabelFn={(option) =>
              t(
                `forms.fields.team.customDomainAttributes.headerConfig.titleType.options.${option}`,
              )
            }
            options={subtitleTypeOptions}
            customPlaceholder={subtitlePlaceholder}
            onChange={(value) => {
              previewHeaderConfig('subtitleType', value as string)
            }}
          />

          <NumberField
            layout='box'
            name='customDomainAttributes.headerConfig.height'
            customPlaceholder='160'
            onBlur={(e) => {
              previewHeaderConfig(
                'height',
                (e.target.value ||
                  160) as wowaudit.CustomDomain['headerConfig']['height'],
              )
            }}
          />
        </FormWrapper>
      </OverviewRuntimeContext.Provider>

      <CustomDomainNewHostModal
        open={modalOpen}
        setOpen={setModalOpen}
        entityType={entityType}
        entity={entity}
      />
    </>
  )
}

const CustomDomainSection = () => {
  const { guild, team } = useTeam()
  const { customDomain } = useCustomDomain()

  if (customDomain?.guild && !customDomain.team) {
    return (
      <Alert variant='info'>
        <AlertTitle>Team domain settings unavailable</AlertTitle>
        <AlertDescription>
          You can only manage the custom domain for your{' '}
          <Link
            to={`${customDomain.guild.path || ''}/settings#custom_domain`}
            className='text-primary hover:text-primary/90'
          >
            guild
          </Link>{' '}
          from this site. To manage the custom domains of this team, visit it
          from the main wowaudit website.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <CustomDomainSettingsSection
      entityType='team'
      entity={team}
      guild={guild}
      team={team}
      ownerLabel='team'
      premiumPath={`${team.path}/settings#premium`}
      titleTypeOptions={customDomainHeaderTitleTypes}
      subtitleTypeOptions={customDomainHeaderTitleTypes}
      subtitlePlaceholder='Team description'
      uploadOwnerFile={async (file) => (await uploadFile(team.id, file)).url}
    />
  )
}

export default CustomDomainSection
